import utilSvc from '../../utilSvc';
import { formatLocalDate } from '../../components/validation/ValidInputDate.utils';
import { calendarDayDiff } from './calendarDayDiff';

export type StartChangeEffect = {
    /** New startError value to set, or null to leave unchanged. */
    startError: string | null;
    /** New endError value to set, or null to leave unchanged. */
    endError: string | null;
    /**
     * When true: apply batchWrite (StartDate + clampedEnd) then return —
     * skip end revalidation and the final batchSet.
     */
    earlyReturn: boolean;
    /** Date writes for batchSet when earlyReturn is true. */
    batchWrite: Record<string, string> | null;
    /**
     * When earlyReturn is false, these are the values to batchSet.
     */
    finalStartDate: string | null;
    finalEndDate: string | null;
    /**
     * Extra EndDate write when the endError-clearing arm fires
     * (else if (endError) branch inside the end revalidation block).
     */
    extraEndDateWrite: string | null;
};

// ---------------------------------------------------------------------------
// Pure computation for handleStartChange decision logic.
// All inputs are zeroTime-normalised dates (except rawEnd — the original
// dateRangeEnd before today-clamping). Returns an effect descriptor with
// no side effects — the hook applies the effect.
// ---------------------------------------------------------------------------
export function computeStartChangeEffect(
    s: Date,
    e: Date,
    today: Date,
    maxDays: number,
    currentStartError: string,
    currentEndError: string,
    rawEnd: Date, // original dateRangeEnd, before today-clamping
): StartChangeEffect {
    const clampedEnd = utilSvc.dt.addDays(s, maxDays - 1);
    // Mirror the hook: clamp end to today if it's in the future
    const end = rawEnd > today ? today : e;
    const diffDays = calendarDayDiff(s, end);

    // Main start-error branch
    if (s > e) {
        // Also revalidate end error: e < s
        return {
            startError: 'Start date must not be after end date.',
            endError: 'End date must not be before start date.',
            earlyReturn: false,
            batchWrite: null,
            finalStartDate: formatLocalDate(s),
            finalEndDate: formatLocalDate(end),
            extraEndDateWrite: null,
        };
    } else if (s > today) {
        // Also revalidate end error: e > today (since s > today and s <= e implies e > today too)
        return {
            startError: 'Start date cannot be in the future.',
            endError: 'End date cannot be in the future.',
            earlyReturn: false,
            batchWrite: null,
            finalStartDate: formatLocalDate(s),
            finalEndDate: formatLocalDate(end),
            extraEndDateWrite: null,
        };
    } else if (e > today) {
        // end is in future, but start is not; no endError clear needed separately
        return {
            startError: 'End date cannot be in the future.',
            endError: null,
            earlyReturn: false,
            batchWrite: null,
            finalStartDate: formatLocalDate(s),
            finalEndDate: formatLocalDate(end),
            extraEndDateWrite: null,
        };
    } else if (currentStartError) {
        // Clearing start error — end revalidation follows
        // end revalidation: e < s → false (e >= s), e > today → false, endError → check
        const endError = currentEndError ? '' : null;
        const extraEndDateWrite = currentEndError ? formatLocalDate(clampedEnd) : null;
        return {
            startError: '',
            endError,
            earlyReturn: false,
            batchWrite: null,
            finalStartDate: formatLocalDate(s),
            finalEndDate: formatLocalDate(end),
            extraEndDateWrite,
        };
    } else if (diffDays > maxDays - 1) {
        return {
            startError: 'Date range cannot exceed ' + maxDays + ' days.',
            endError: null,
            earlyReturn: true,
            batchWrite: { StartDate: formatLocalDate(s), EndDate: formatLocalDate(clampedEnd) },
            finalStartDate: null,
            finalEndDate: null,
            extraEndDateWrite: null,
        };
    }

    // Happy path — end revalidation: none of the conditions fire
    const endError = currentEndError ? '' : null;
    const extraEndDateWrite = currentEndError ? formatLocalDate(clampedEnd) : null;
    return {
        startError: null,
        endError,
        earlyReturn: false,
        batchWrite: null,
        finalStartDate: formatLocalDate(s),
        finalEndDate: formatLocalDate(end),
        extraEndDateWrite,
    };
}
